import type { AxiosInstance } from 'axios';

import type { AudioDevice } from './contexts/microphone';
import type {
  ClinicalNote,
  CreateStandalonePatientVisitRequest,
  CreateStandalonePatientVisitResponse,
  QuickNotePatientData,
} from './types/api';

/**
 * Dependencies required for API calls
 */
export interface UploadDependencies {
  /**
   * @deprecated Use axiosInstance instead for proper token refresh handling
   */
  getAccessToken?: () => string | null | Promise<string | null>;
  apiUrl: string;
  /**
   * Axios instance with auth interceptors for automatic token refresh.
   * When provided, this is preferred over getAccessToken for API calls.
   */
  axiosInstance?: AxiosInstance;
}

/**
 * Helper function to make API requests using axios instance if available,
 * otherwise falls back to fetch with the provided access token.
 */
const makeRequest = async <T>(
  url: string,
  options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers: Record<string, string>;
    body?: string;
  },
  deps: UploadDependencies,
): Promise<T> => {
  // Use axios instance if provided (preferred - has token refresh interceptors)
  if (deps.axiosInstance) {
    const response = await deps.axiosInstance.request<T>({
      url,
      method: options.method,
      headers: options.headers,
      data: options.body ? JSON.parse(options.body) : undefined,
    });
    return response.data;
  }

  // Fallback to fetch (legacy - no automatic token refresh)
  const response = await fetch(url, options);
  if (!response.ok) {
    // Try to get error message from response body
    let errorMessage = response.statusText;
    try {
      const errorBody = await response.json();
      // Extract nested error code (e.g. 422 EMPTY_TRANSCRIPT from nucleus)
      const nestedCode = errorBody?.data?.data?.detail?.code;
      if (nestedCode) {
        errorMessage = nestedCode;
      } else if (errorBody.message) {
        errorMessage = errorBody.message;
      } else if (errorBody.error) {
        errorMessage = errorBody.error;
      } else if (typeof errorBody === 'string') {
        errorMessage = errorBody;
      }
    } catch {
      // If we can't parse the body, try to get it as text
      try {
        const textBody = await response.text();
        if (textBody) {
          errorMessage = textBody;
        }
      } catch {
        // Keep the statusText if we can't read the body
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * This converts a snake_case prompt name into a more friendly version.
 *
 * @param promptName - Name of a preset Template that is shown to the user, used
 * to decide which instruction prompt to run the transcribed audio against.
 * @returns The prompt name string, with multiple words separated by a space, and
 * all words capitalized.
 */
export const displayHumanReadableTemplateName = (note: ClinicalNote) => {
  if (note.templateName) {
    return note.templateName;
  }
  if (note.promptName) {
    const promptName = note.promptName
      .split('_')
      .map(
        (chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase(),
      )
      .join(' ');
    return promptName;
  }
  return 'N/A';
};

/**
 * Template selection is only available before recording begins (idle) or after
 * a stopped recording has been reset, and only while there is no captured blob.
 */
export const shouldShowTemplateSelection = ({
  mediaBlobUrl,
  recorderStatus,
}: {
  mediaBlobUrl?: string | null;
  recorderStatus: string;
}): boolean =>
  !mediaBlobUrl &&
  (recorderStatus === 'idle' || recorderStatus === 'stopped');

//--- Helper functions related to actually saving and generating the clinical note

/**
 * The size of each file chunk to upload, set to 5MB (the minimum part size for AWS S3 multipart uploads).
 */
export const CHUNK_SIZE = 5 * 1024 * 1024;

/**
 * Maps MIME types to file extensions.
 */
const mimeToExtension: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/wave': 'wav',
  'audio/x-wav': 'wav',
  'audio/m4a': 'm4a',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'audio/flac': 'flac',
};

/**
 * Gets the file extension from a MIME type.
 */
const getExtensionFromMimeType = (mimeType: string): string => {
  return mimeToExtension[mimeType] || 'webm';
};

/**
 * Converts a media blob URL into a `File` object with the appropriate extension
 * based on the blob's MIME type.
 *
 * @param {string} mediaBlobUrl - The URL of the media blob to convert.
 * @param {string | undefined} id - An optional identifier (e.g. appointment ID) used to name the file.
 * @returns {Promise<File>} A promise that resolves to a `File` object.
 */
export const convertMediaBlobToFile = async (
  mediaBlobUrl: string,
  id: string | undefined,
): Promise<File> => {
  const blob = await fetch(mediaBlobUrl).then((r) => r.blob());
  const fileName = id === undefined ? 'no-appointment-id' : id;

  // Determine the file extension based on the blob's MIME type
  const mimeType = blob.type || 'audio/webm';
  const extension = getExtensionFromMimeType(mimeType);

  return new File([blob], `${fileName}.${extension}`, { type: mimeType });
};

/**
 * Constructs the HTTP headers required for API requests, including authorization and contextual identifiers.
 * When using axios instance, the Authorization header is handled by interceptors.
 *
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @param {UploadDependencies} deps - Dependencies for getting access token and API URL.
 * @returns {Promise<Record<string, string>>} A promise that resolves to HTTP headers.
 */
const getHeaders = async (
  currentOrganizationId: any,
  currentPracticeId: any,
  deps: UploadDependencies,
): Promise<{ [key: string]: string }> => {
  const headers: Record<string, string> = {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
    practice: currentPracticeId,
    org: currentOrganizationId,
  };

  // Only add Authorization header if using legacy getAccessToken (not axios)
  // When using axios instance, the interceptors handle the Authorization header
  if (!deps.axiosInstance && deps.getAccessToken) {
    const token = await deps.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Initiates a multipart upload by requesting an upload ID and key from the server.
 *
 * @param {string} filename - The name of the file to upload.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @param {UploadDependencies} deps - Dependencies for getting access token and API URL.
 * @returns {Promise<{ uploadId: string; key: string }>} A promise that resolves to the upload metadata.
 */
export const initiateUpload = async (
  filename: string,
  currentOrganizationId: any,
  currentPracticeId: any,
  deps: UploadDependencies,
): Promise<{ uploadId: string; key: string }> => {
  return makeRequest<{ uploadId: string; key: string }>(
    `${deps.apiUrl}/videa-notes/initiate-upload`,
    {
      method: 'POST',
      headers: await getHeaders(currentOrganizationId, currentPracticeId, deps),
      body: JSON.stringify({ filename }),
    },
    deps,
  );
};

/**
 * Retrieves presigned URLs to upload each part of a file during a multipart upload.
 *
 * @param {string} key - The S3 key of the file.
 * @param {string} uploadId - The upload ID for the multipart session.
 * @param {number} partsCount - The total number of parts the file will be split into.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @param {UploadDependencies} deps - Dependencies for getting access token and API URL.
 * @returns {Promise<Array<{ signedUrl: string; partNumber: number }>>} A promise that resolves to an array of presigned URLs and part numbers.
 */
export const getPresignedUrls = async (
  key: string,
  uploadId: string,
  partsCount: number,
  currentOrganizationId: any,
  currentPracticeId: any,
  deps: UploadDependencies,
): Promise<Array<{ signedUrl: string; partNumber: number }>> => {
  const data = await makeRequest<{
    urls: Array<{ signedUrl: string; partNumber: number }>;
  }>(
    `${deps.apiUrl}/videa-notes/presigned-urls`,
    {
      method: 'POST',
      headers: await getHeaders(currentOrganizationId, currentPracticeId, deps),
      body: JSON.stringify({ key, uploadId, partsCount }),
    },
    deps,
  );
  return data.urls; // array of { signedUrl, partNumber }
};

/**
 * Uploads individual file parts to presigned URLs during a multipart upload.
 *
 * @param {File} file - The file to upload.
 * @param {Array<{ signedUrl: string; partNumber: number }>} urls - An array of presigned URLs and corresponding part numbers.
 * @returns {Promise<Array<{ ETag: string | null; PartNumber: number }>>} A promise that resolves to an array of ETags and part numbers for completing the upload.
 * @throws Will throw an error if any part upload fails.
 */
export const uploadFileParts = async (
  file: File,
  urls: Array<{ signedUrl: string; partNumber: number }>,
): Promise<Array<{ ETag: string | null; PartNumber: number }>> => {
  const etags: { ETag: string | null; PartNumber: number }[] = [];

  for (const { partNumber, signedUrl } of urls) {
    const start = (partNumber - 1) * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blobPart = file.slice(start, end);

    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: blobPart,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload part ${partNumber}`);
    }

    const etag = response.headers.get('ETag');
    etags.push({ ETag: etag, PartNumber: partNumber });
  }

  return etags;
};

/**
 * Completes a multipart upload by notifying the backend that all parts have been uploaded.
 *
 * @param {string} key - The S3 key of the file.
 * @param {string} uploadId - The multipart upload ID.
 * @param {Array<{ ETag: string | null; PartNumber: number }>} etags - The list of ETags and part numbers for each uploaded part.
 * @param {string | undefined} patientConsentRecordId - The patient consent record ID associated with the note.
 * @param {string | undefined} unifiedAppointmentId - The appointment ID associated with the note.
 * @param {string | undefined} standalonePatientVisitId - The standalone patient visit ID associated with the note.
 * @param {string | undefined} selectedPrompt - The selected prompt/template name.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @param {UploadDependencies} deps - Dependencies for getting access token and API URL.
 * @param {AudioDevice[]} availableAudioDevices - The list of audio input devices available at the time of recording.
 * @param {string | null} selectedAudioDeviceId - The ID of the audio device selected for recording, or null if none was selected.
 * @returns {Promise<ClinicalNote>} A promise that resolves to the created clinical note.
 */
export const completeUpload = async ({
  key,
  uploadId,
  etags,
  patientConsentRecordId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  deps,
  templateId,
  availableAudioDevices,
  selectedAudioDeviceId,
  language,
}: {
  key: string;
  uploadId: string;
  etags: Array<{ ETag: string | null; PartNumber: number }>;
  patientConsentRecordId: string | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  currentOrganizationId: any;
  currentPracticeId: any;
  deps: UploadDependencies;
  templateId?: string | undefined;
  availableAudioDevices: AudioDevice[];
  selectedAudioDeviceId: string | null;
  language?: string;
}): Promise<ClinicalNote> => {
  const requestBody: any = {
    key,
    uploadId,
    parts: etags,
    patientConsentRecordId,
    availableAudioDevices,
    selectedAudioDeviceId,
    templateId,
    ...(language && { language }),
  };

  // Add the appropriate ID based on which one is available
  if (unifiedAppointmentId) {
    requestBody.unifiedAppointmentId = unifiedAppointmentId;
  } else if (standalonePatientVisitId) {
    requestBody.standalonePatientVisitId = standalonePatientVisitId;
  }

  return makeRequest<ClinicalNote>(
    `${deps.apiUrl}/videa-notes/complete-upload`,
    {
      method: 'POST',
      headers: await getHeaders(currentOrganizationId, currentPracticeId, deps),
      body: JSON.stringify(requestBody),
    },
    deps,
  );
};

export enum PatientConsentStatus {
  YES = 'YES',
  NO = 'NO',
  WITHDRAWN = 'WITHDRAWN',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export type MarkConsentRequest = {
  consentStatus: PatientConsentStatus;
  isGivenByPatient: boolean;
  patientFullName: string;
  patientAppointmentAt: string;
  recordedBy: string;
  patientDob?: string;
};

export const createPatientConsentRecord = async ({
  consentRequest,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  deps,
}: {
  consentRequest: MarkConsentRequest;
  unifiedAppointmentId?: number;
  standalonePatientVisitId?: string;
  currentOrganizationId: any;
  currentPracticeId: any;
  deps: UploadDependencies;
}): Promise<{ id: string }> => {
  // Only send one of the IDs to avoid API error
  const requestBody: any = {
    consentStatus: consentRequest.consentStatus,
    isGivenByPatient: consentRequest.isGivenByPatient,
    patientFullName: consentRequest.patientFullName,
    patientAppointmentAt: consentRequest.patientAppointmentAt,
    recordedBy: consentRequest.recordedBy,
    patientDob: consentRequest.patientDob,
  };

  // Add the appropriate ID based on which one is available
  if (unifiedAppointmentId) {
    requestBody.unifiedAppointmentId = unifiedAppointmentId;
  } else if (standalonePatientVisitId) {
    requestBody.standalonePatientVisitId = standalonePatientVisitId;
  }

  return makeRequest<{ id: string }>(
    `${deps.apiUrl}/patient-consent-records`,
    {
      method: 'POST',
      headers: await getHeaders(currentOrganizationId, currentPracticeId, deps),
      body: JSON.stringify(requestBody),
    },
    deps,
  );
};

//--- Quick Note / Standalone Patient Visit utilities

/**
 * Validates if a full name string contains at least a valid first name.
 * Extracts the first name (text before first space, or entire string if no space)
 * and checks that it has at least one character.
 */
export const isNameValid = (fullName: string): boolean => {
  const trimmed = fullName.trim();
  if (!trimmed) return false;

  const spaceIndex = trimmed.indexOf(' ');
  const firstName = spaceIndex > 0 ? trimmed.substring(0, spaceIndex) : trimmed;

  return firstName.length > 0;
};

/**
 * Helper function to get the nearest half-hour time in HH:MM format.
 */
export const getNearestHalfHour = (): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const hours = now.getHours();

  // Round to nearest half-hour
  const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
  const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours;

  return `${roundedHours.toString().padStart(2, '0')}:${roundedMinutes
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Parses a full name string into first and last name components.
 */
export const parseFullName = (
  fullName: string,
): { firstName: string; lastName: string | undefined } => {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(' ');

  return {
    firstName: spaceIndex > 0 ? trimmed.substring(0, spaceIndex) : trimmed,
    lastName: spaceIndex > 0 ? trimmed.substring(spaceIndex + 1) : undefined,
  };
};

/**
 * Prepares the data for creating a standalone patient visit from form inputs.
 */
export const prepareQuickNoteVisitData = (
  fullName: string,
  sourcePatientId?: string,
  appointmentDate?: Date,
): CreateStandalonePatientVisitRequest => {
  const { firstName, lastName } = parseFullName(fullName);
  const appointmentTime = getNearestHalfHour();
  const [hours, minutes] = appointmentTime.split(':').map(Number);

  const date = appointmentDate || new Date();
  const localDateTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  );

  const appointmentStartTimeUtc =
    localDateTime.toISOString().split('T')[1]?.slice(0, 8) ?? '00:00:00';

  return {
    firstName: firstName.trim(),
    lastName: lastName?.trim() || undefined,
    sourcePatientId: sourcePatientId?.trim() || undefined,
    appointmentDate: date.toISOString(),
    appointmentStartTime: appointmentStartTimeUtc,
    appointmentOn: localDateTime.toISOString(),
  };
};

/**
 * Converts a CreateStandalonePatientVisitResponse to QuickNotePatientData.
 */
export const toQuickNotePatientData = (
  response: CreateStandalonePatientVisitResponse,
): QuickNotePatientData => ({
  id: response.id,
  firstName: response.firstName,
  lastName: response.lastName,
  sourcePatientId: response.sourcePatientId,
  dateOfBirth: response.dateOfBirth,
  displayName: response.lastName
    ? `${response.firstName} ${response.lastName}`
    : response.firstName,
});
